import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './App.css';

// API Base URL
const API_BASE = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';

// Generate unique session ID
const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
};

// ============================================
// COMPONENT: Business Recommendation Card
// ============================================
const BusinessCard = ({ business, onSelect }) => {
  const getRiskColor = (risk) => {
    switch (risk) {
      case 'low': return '#10b981';
      case 'medium': return '#f59e0b';
      case 'high': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE').format(amount);
  };

  return (
    <div className="business-card">
      <div className="business-card-header">
        <h3>{business.name}</h3>
        <span
          className="risk-badge"
          style={{ backgroundColor: getRiskColor(business.risk_level) }}
        >
          {business.risk_level} risk
        </span>
      </div>
      <p className="business-tagline">{business.tagline}</p>

      <div className="business-stats">
        <div className="stat">
          <span className="stat-label">Startup Capital</span>
          <span className="stat-value">
            KES {formatCurrency(business.capital_required_low)} - {formatCurrency(business.capital_required_high)}
          </span>
        </div>
        <div className="stat">
          <span className="stat-label">Monthly Profit</span>
          <span className="stat-value profit">
            KES {formatCurrency(business.monthly_profit_low)} - {formatCurrency(business.monthly_profit_high)}
          </span>
        </div>
        <div className="stat">
          <span className="stat-label">Time Commitment</span>
          <span className="stat-value">{business.time_commitment}</span>
        </div>
      </div>

      <div className="skills-needed">
        <span className="skills-label">Skills Needed:</span>
        <div className="skills-tags">
          {business.skills_needed.map((skill, idx) => (
            <span key={idx} className="skill-tag">{skill}</span>
          ))}
        </div>
      </div>

      <p className="why-suitable">{business.why_suitable}</p>

      <div className="quick-steps">
        <h4>Quick Start Steps:</h4>
        <ol>
          {business.quick_start_steps.map((step, idx) => (
            <li key={idx}>{step}</li>
          ))}
        </ol>
      </div>

      <button className="select-business-btn" onClick={() => onSelect(business.name)}>
        Tell me more about this business
      </button>
    </div>
  );
};

// ============================================
// COMPONENT: Budget Breakdown Table
// ============================================
const BudgetTable = ({ items, totalLow, totalHigh }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE').format(amount);
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'essential': return '🔴';
      case 'recommended': return '🟡';
      case 'optional': return '🟢';
      default: return '⚪';
    }
  };

  return (
    <div className="budget-container">
      <div className="budget-header">
        <h3>Startup Budget Breakdown</h3>
        <div className="budget-legend">
          <span>🔴 Essential</span>
          <span>🟡 Recommended</span>
          <span>🟢 Optional</span>
        </div>
      </div>
      <table className="budget-table">
        <thead>
          <tr>
            <th>Item</th>
            <th>Description</th>
            <th>Cost Range (KES)</th>
            <th>Priority</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <tr key={idx} className={`priority-${item.priority}`}>
              <td className="item-name">{item.item}</td>
              <td className="item-desc">{item.description}</td>
              <td className="item-cost">
                {formatCurrency(item.cost_low)} - {formatCurrency(item.cost_high)}
              </td>
              <td className="item-priority">{getPriorityIcon(item.priority)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="total-row">
            <td colSpan="2"><strong>TOTAL ESTIMATED BUDGET</strong></td>
            <td colSpan="2" className="total-amount">
              <strong>KES {formatCurrency(totalLow)} - {formatCurrency(totalHigh)}</strong>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

// ============================================
// COMPONENT: License Guide
// ============================================
const LicenseGuide = ({ licenses }) => {
  return (
    <div className="license-container">
      <h3>Required Licenses & Permits</h3>
      {licenses.map((license, idx) => (
        <div key={idx} className="license-card">
          <div className="license-header">
            <h4>{license.name}</h4>
            <div className="license-meta">
              <span className="license-cost">{license.total_cost}</span>
              <span className="license-time">{license.processing_time}</span>
            </div>
          </div>
          <p className="license-authority">Issued by: {license.authority}</p>

          <div className="license-steps">
            <h5>Steps to Obtain:</h5>
            {license.steps.map((step, stepIdx) => (
              <div key={stepIdx} className="license-step">
                <div className="step-number">{step.step_number}</div>
                <div className="step-content">
                  <p className="step-action">{step.action}</p>
                  <div className="step-details">
                    <span>📍 {step.where}</span>
                    <span>💰 {step.cost}</span>
                    <span>⏱️ {step.duration}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================
// COMPONENT: Supplier Directory
// ============================================
const SupplierDirectory = ({ suppliers }) => {
  return (
    <div className="supplier-container">
      <h3>Where to Buy</h3>
      <div className="supplier-grid">
        {suppliers.map((supplier, idx) => (
          <div key={idx} className="supplier-card">
            <h4>{supplier.name}</h4>
            <p className="supplier-location">📍 {supplier.location}</p>
            <p className="supplier-items">🛒 {supplier.what_to_buy}</p>
            <p className="supplier-price">💰 {supplier.price_range}</p>
            <div className="supplier-tip">
              <span className="tip-icon">💡</span>
              <p>{supplier.tips}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// COMPONENT: Profit Projection Chart
// ============================================
const ProfitProjection = ({ projections }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE').format(amount);
  };

  const maxValue = Math.max(...projections.map(p => Math.max(p.revenue, p.expenses)));

  return (
    <div className="projection-container">
      <h3>Profit & Loss Projection</h3>
      <div className="projection-chart">
        {projections.map((proj, idx) => (
          <div key={idx} className="projection-month">
            <div className="bars">
              <div
                className="bar revenue-bar"
                style={{ height: `${(proj.revenue / maxValue) * 100}%` }}
                title={`Revenue: KES ${formatCurrency(proj.revenue)}`}
              />
              <div
                className="bar expense-bar"
                style={{ height: `${(proj.expenses / maxValue) * 100}%` }}
                title={`Expenses: KES ${formatCurrency(proj.expenses)}`}
              />
            </div>
            <span className="month-label">M{proj.month}</span>
          </div>
        ))}
      </div>
      <div className="projection-legend">
        <span className="legend-item"><span className="legend-color revenue"></span> Revenue</span>
        <span className="legend-item"><span className="legend-color expense"></span> Expenses</span>
      </div>
      <table className="projection-table">
        <thead>
          <tr>
            <th>Month</th>
            <th>Revenue</th>
            <th>Expenses</th>
            <th>Profit</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {projections.map((proj, idx) => (
            <tr key={idx}>
              <td>Month {proj.month}</td>
              <td className="amount">KES {formatCurrency(proj.revenue)}</td>
              <td className="amount">KES {formatCurrency(proj.expenses)}</td>
              <td className={`amount ${proj.profit >= 0 ? 'profit' : 'loss'}`}>
                KES {formatCurrency(proj.profit)}
              </td>
              <td className="notes">{proj.notes}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ============================================
// COMPONENT: Action Plan Timeline
// ============================================
const ActionPlan = ({ steps }) => {
  return (
    <div className="action-plan-container">
      <h3>Your Action Plan</h3>
      <div className="timeline">
        {steps.map((step, idx) => (
          <div key={idx} className="timeline-item">
            <div className="timeline-marker">
              <div className="marker-dot"></div>
              {idx < steps.length - 1 && <div className="marker-line"></div>}
            </div>
            <div className="timeline-content">
              <div className="timeline-header">
                <span className="timeline-week">{step.week}</span>
                <span className="timeline-cost">{step.estimated_cost}</span>
              </div>
              <h4>{step.title}</h4>
              <ul>
                {step.tasks.map((task, taskIdx) => (
                  <li key={taskIdx}>{task}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// COMPONENT: Follow-up Questions
// ============================================
const FollowUpQuestions = ({ questions, onSelect }) => {
  return (
    <div className="followup-container">
      {questions.map((q, idx) => (
        <div key={idx} className="followup-question">
          <p className="question-text">{q.question}</p>
          <p className="question-why">{q.why_asking}</p>
          {q.options && (
            <div className="question-options">
              {q.options.map((option, optIdx) => (
                <button
                  key={optIdx}
                  className="option-btn"
                  onClick={() => onSelect(option)}
                >
                  {option}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ============================================
// COMPONENT: Chat Message
// ============================================
const ChatMessage = ({ message, onOptionSelect }) => {
  const { role, content, structured_data } = message;

  if (role === 'user') {
    return (
      <div className="message user-message">
        <div className="message-avatar user-avatar">You</div>
        <div className="message-content">
          <p>{content}</p>
        </div>
      </div>
    );
  }

  // Assistant message with structured data
  const data = structured_data || {};

  return (
    <div className="message assistant-message">
      <div className="message-avatar assistant-avatar">BB</div>
      <div className="message-content">
        <p className="main-message">{data.message || content}</p>

        {/* Render structured components based on response type */}
        {data.business_recommendations && data.business_recommendations.length > 0 && (
          <div className="business-recommendations">
            {data.business_recommendations.map((biz, idx) => (
              <BusinessCard
                key={idx}
                business={biz}
                onSelect={onOptionSelect}
              />
            ))}
          </div>
        )}

        {data.budget_items && data.budget_items.length > 0 && (
          <BudgetTable
            items={data.budget_items}
            totalLow={data.budget_total_low}
            totalHigh={data.budget_total_high}
          />
        )}

        {data.licenses && data.licenses.length > 0 && (
          <LicenseGuide licenses={data.licenses} />
        )}

        {data.suppliers && data.suppliers.length > 0 && (
          <SupplierDirectory suppliers={data.suppliers} />
        )}

        {data.monthly_projections && data.monthly_projections.length > 0 && (
          <ProfitProjection projections={data.monthly_projections} />
        )}

        {data.action_steps && data.action_steps.length > 0 && (
          <ActionPlan steps={data.action_steps} />
        )}

        {data.follow_up_questions && data.follow_up_questions.length > 0 && (
          <FollowUpQuestions
            questions={data.follow_up_questions}
            onSelect={onOptionSelect}
          />
        )}

        {data.next_suggested_topic && (
          <div className="next-topic">
            <span className="next-topic-label">Next:</span>
            <button
              className="next-topic-btn"
              onClick={() => onOptionSelect(data.next_suggested_topic)}
            >
              {data.next_suggested_topic}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// MAIN APP COMPONENT
// ============================================
function App() {
  const [sessionId, setSessionId] = useState('');
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const chatEndRef = useRef(null);

  // Initialize session
  useEffect(() => {
    let storedSessionId = localStorage.getItem('biashara_session_id');
    if (!storedSessionId) {
      storedSessionId = generateSessionId();
      localStorage.setItem('biashara_session_id', storedSessionId);
    }
    setSessionId(storedSessionId);
    loadHistory(storedSessionId);
    checkConnection();
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Check API connection
  const checkConnection = async () => {
    try {
      await axios.get(`${API_BASE}/api/health`);
      setConnectionStatus('connected');
    } catch (error) {
      setConnectionStatus('error');
    }
  };

  // Load chat history
  const loadHistory = async (sid) => {
    try {
      const response = await axios.get(`${API_BASE}/api/history?session_id=${sid}`);
      if (response.data.messages && response.data.messages.length > 0) {
        setMessages(response.data.messages);
      }
      setConnectionStatus('connected');
    } catch (error) {
      console.error('Failed to load history:', error);
      setConnectionStatus('error');
    }
  };

  // Send message
  const sendMessage = async (text) => {
    const messageText = text || inputMessage.trim();
    if (!messageText || isLoading) return;

    setInputMessage('');
    setIsLoading(true);

    // Add user message immediately
    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      const response = await axios.post(`${API_BASE}/api/query`, {
        session_id: sessionId,
        message: messageText
      });

      // Add assistant response
      const assistantMessage = {
        role: 'assistant',
        content: response.data.response.message,
        structured_data: response.data.response,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('Failed to send message:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle option selection from components
  const handleOptionSelect = (option) => {
    sendMessage(option);
  };

  // Reset conversation
  const resetConversation = async () => {
    if (!window.confirm('Start a new conversation? This will clear your current session.')) {
      return;
    }

    try {
      await axios.post(`${API_BASE}/api/reset`, { session_id: sessionId });
    } catch (error) {
      console.error('Failed to reset on server:', error);
    }

    const newSessionId = generateSessionId();
    localStorage.setItem('biashara_session_id', newSessionId);
    setSessionId(newSessionId);
    setMessages([]);
  };

  // Export conversation
  const exportConversation = async () => {
    try {
      const response = await axios.post(`${API_BASE}/api/export`, {
        session_id: sessionId
      });

      const blob = new Blob([response.data.text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `biashara-buddy-consultation-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export:', error);
      alert('Failed to export conversation');
    }
  };

  // Handle Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="app-container">
      {/* Header */}
      <header className="app-header">
        <div className="header-brand">
          <div className="logo">BB</div>
          <div className="brand-text">
            <h1>Biashara Buddy</h1>
            <p>Your Kenya Business Consultant</p>
          </div>
        </div>
        <div className="header-actions">
          <div className={`connection-status ${connectionStatus}`}>
            <span className="status-dot"></span>
            <span className="status-text">
              {connectionStatus === 'connected' ? 'Online' :
               connectionStatus === 'connecting' ? 'Connecting...' : 'Offline'}
            </span>
          </div>
          <button className="header-btn export-btn" onClick={exportConversation} title="Export Chat">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
            </svg>
          </button>
          <button className="header-btn reset-btn" onClick={resetConversation} title="New Chat">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 4v16m8-8H4"/>
            </svg>
          </button>
        </div>
      </header>

      {/* Chat Window */}
      <main className="chat-window">
        {messages.length === 0 ? (
          <div className="welcome-screen">
            <div className="welcome-icon">🇰🇪</div>
            <h2>Karibu! Welcome to Biashara Buddy</h2>
            <p>I'm here to help you start and grow a successful business in Kenya.</p>

            <div className="feature-cards">
              <div className="feature-card">
                <span className="feature-icon">💡</span>
                <h3>Business Ideas</h3>
                <p>Get personalized business recommendations based on your capital and skills</p>
              </div>
              <div className="feature-card">
                <span className="feature-icon">📊</span>
                <h3>Budget Planning</h3>
                <p>Detailed startup budgets with itemized costs</p>
              </div>
              <div className="feature-card">
                <span className="feature-icon">📋</span>
                <h3>License Guide</h3>
                <p>Step-by-step licensing and registration help</p>
              </div>
              <div className="feature-card">
                <span className="feature-icon">📈</span>
                <h3>Profit Projections</h3>
                <p>Realistic revenue and expense forecasts</p>
              </div>
            </div>

            <div className="quick-starts">
              <p>Get started:</p>
              <div className="quick-start-buttons">
                <button onClick={() => sendMessage("I want to start a business but I don't know where to begin")}>
                  I'm new to business
                </button>
                <button onClick={() => sendMessage("I have KES 50,000 to invest. What business can I start?")}>
                  I have KES 50K
                </button>
                <button onClick={() => sendMessage("What are the best businesses in Nairobi right now?")}>
                  Best businesses in Nairobi
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="messages-container">
            {messages.map((msg, idx) => (
              <ChatMessage
                key={idx}
                message={msg}
                onOptionSelect={handleOptionSelect}
              />
            ))}
            {isLoading && (
              <div className="message assistant-message loading">
                <div className="message-avatar assistant-avatar">BB</div>
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
        )}
      </main>

      {/* Input Area */}
      <footer className="input-area">
        <div className="input-container">
          <textarea
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything about starting a business in Kenya..."
            rows="1"
            disabled={isLoading}
          />
          <button
            className="send-btn"
            onClick={() => sendMessage()}
            disabled={!inputMessage.trim() || isLoading}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
            </svg>
          </button>
        </div>
        <p className="input-hint">
          Press Enter to send. Biashara Buddy gives advice specific to Kenya's market.
        </p>
      </footer>
    </div>
  );
}

export default App;
